import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { LENDERS } from "@/lib/lenders";
import { FeaturedBank } from "./FeaturedBank";
import { ArticleCalculator } from "./ArticleCalculator";
import { LenderReviews } from "./LenderReviews";
import { LenderTable } from "./LenderTable";
import { EpiProvider } from "@/lib/epi";
import type { Post } from "@/lib/news/types";

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

/** Enkel inline-markup: [text](/sida) blir en intern länk, http(s) blir extern. */
function renderText(text: string, key: string) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return (
    <>
      {parts.map((part, i) => {
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (!m) return <span key={`${key}-${i}`}>{part}</span>;
        const href = m[2]!;
        if (/^https?:\/\//.test(href)) {
          return (
            <a
              key={`${key}-${i}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary underline underline-offset-2"
            >
              {m[1]}
            </a>
          );
        }
        return (
          <Link
            key={`${key}-${i}`}
            to={href as any}
            className="font-semibold text-primary underline underline-offset-2"
          >
            {m[1]}
          </Link>
        );
      })}
    </>
  );
}

export function ArticleBody({
  post,
  section,
  calculatorPosition = "top",
  epi,
}: {
  post: Post;
  section: { label: string; to: string };
  /** "top" = ovanför första företagskortet, "afterReviews" = under recensionerna. */
  calculatorPosition?: "top" | "afterReviews";
  epi: string;
}) {
  const topLender = { ...LENDERS[post.topLenderId], badge: "MEST POPULÄR" };
  const bottomLender = { ...LENDERS[post.bottomLenderId], badge: "VÅR REKOMMENDATION" };
  const toc = post.blocks.filter((b) => b.t === "h2") as { t: "h2"; text: string }[];

  return (
    <EpiProvider value={epi}>
      <>
        <nav aria-label="Brödsmulor" className="not-prose -mt-2 text-sm text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link to="/" className="hover:text-primary">
              Hem
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li>
            <Link to={section.to as any} className="hover:text-primary">
              {section.label}
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li className="text-foreground/70">{post.title}</li>
        </ol>
      </nav>

      {(post.author || post.published) && (
        <div className="not-prose rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">
            {post.author ?? "Svensk Låneguides redaktion"}
          </span>
          {post.authorTitle && <span> – {post.authorTitle}</span>}
          {post.published && <span> · Publicerad {post.published}</span>}
          {post.updated && <span> · Senast uppdaterad {post.updated}</span>}
          <span>
            {" "}
            ·{" "}
            <Link to="/om-oss/redaktion" className="text-primary underline underline-offset-2">
              Om vår redaktion
            </Link>
          </span>
        </div>
      )}

      {post.heroSrc && (
        <img
          src={post.heroSrc}
          alt={post.heroAlt ?? post.title}
          width={1200}
          height={630}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="not-prose w-full rounded-md border border-border object-cover"
        />
      )}

      <p className="text-lg text-foreground/85">{post.intro}</p>

      {toc.length > 3 && (
        <nav aria-label="Innehållsförteckning" className="not-prose rounded-md border border-border bg-card p-5">
          <h2 className="font-display text-base font-extrabold text-foreground">Innehåll</h2>
          <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm">
            {toc.map((h, i) => (
              <li key={i}>
                <a href={`#${slugify(h.text)}`} className="text-primary underline underline-offset-2">
                  {h.text}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      {post.calculator && calculatorPosition === "top" && <ArticleCalculator {...post.calculator} />}

      <div className="not-prose my-8">
        <FeaturedBank
          lender={topLender}
          title={post.topLenderTitle ?? `Mest populär just nu – ${topLender.name}`}
          compact
        />
      </div>

      {post.blocks.map((b, i) => {
        const key = `b-${i}`;
        switch (b.t) {
          case "h2":
            return (
              <h2 key={key} id={slugify(b.text)} className="scroll-mt-24">
                {b.text}
              </h2>
            );
          case "h3":
            return <h3 key={key}>{b.text}</h3>;
          case "p":
            return (
              <p key={key} className="mt-4">
                {renderText(b.text, key)}
              </p>
            );
          case "ul":
            return (
              <ul key={key} className="mt-4 list-disc space-y-2 pl-5">
                {b.items.map((it, j) => (
                  <li key={j}>{renderText(it, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case "img":
            return (
              <img
                key={key}
                src={b.src}
                alt={b.alt}
                width={1280}
                height={720}
                loading="lazy"
                className="my-6 w-full rounded-md border border-border object-cover"
              />
            );
          case "table":
            return (
              <figure key={key} className="not-prose my-6 overflow-hidden rounded-md border border-border">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left text-sm">
                    <thead className="bg-muted/60">
                      <tr>
                        {b.head.map((h, j) => (
                          <th key={j} className="border-b border-border px-4 py-2.5 font-display text-[13px] font-extrabold text-foreground">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {b.rows.map((r, j) => (
                        <tr key={j} className="odd:bg-card even:bg-muted/25">
                          {r.map((c, k) => (
                            <td key={k} className="border-b border-border/60 px-4 py-2.5 align-top text-foreground/80">
                              {renderText(c, `${key}-${j}-${k}`)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {b.caption && (
                  <figcaption className="bg-card px-4 py-2 text-xs text-muted-foreground">{b.caption}</figcaption>
                )}
              </figure>
            );
          case "box": {
            const tone = b.tone ?? "info";
            const border =
              tone === "warning" ? "border-destructive/40" : tone === "tip" ? "border-primary/40" : "border-border";
            return (
              <aside key={key} className={`not-prose my-6 rounded-md border ${border} bg-card p-5`}>
                {b.title && (
                  <p className="font-display text-base font-extrabold text-foreground">{b.title}</p>
                )}
                {b.text && (
                  <p className="mt-2 text-[15px] leading-relaxed text-foreground/80">{renderText(b.text, key)}</p>
                )}
                {b.items && (
                  <ul className="mt-3 space-y-1.5 text-[15px] text-foreground/80">
                    {b.items.map((it, j) => (
                      <li key={j} className="flex gap-2">
                        <span aria-hidden="true" className="text-primary">•</span>
                        <span>{renderText(it, `${key}-${j}`)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </aside>
            );
          }
          case "proscons":
            return (
              <div key={key} className="not-prose my-6">
                {b.title && (
                  <p className="mb-3 font-display text-base font-extrabold text-foreground">{b.title}</p>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-md border border-border bg-card p-5">
                    <p className="font-display text-sm font-extrabold text-foreground">Fördelar</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-foreground/80">
                      {b.pros.map((p, j) => (
                        <li key={j} className="flex gap-2">
                          <span aria-hidden="true" className="text-primary">+</span>
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-md border border-border bg-card p-5">
                    <p className="font-display text-sm font-extrabold text-foreground">Nackdelar</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-foreground/80">
                      {b.cons.map((c, j) => (
                        <li key={j} className="flex gap-2">
                          <span aria-hidden="true" className="text-destructive">–</span>
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          case "steps":
            return (
              <div key={key} className="not-prose my-6 rounded-md border border-border bg-card p-5">
                {b.title && (
                  <p className="font-display text-base font-extrabold text-foreground">{b.title}</p>
                )}
                <ol className="mt-3 space-y-2.5">
                  {b.items.map((it, j) => (
                    <li key={j} className="flex gap-3 text-[15px] text-foreground/80">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-bold text-primary">
                        {j + 1}
                      </span>
                      <span>{renderText(it, `${key}-${j}`)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            );
          case "lenders": {
            const list = b.ids
              .map((id) => LENDERS[id])
              .filter((l): l is (typeof LENDERS)[keyof typeof LENDERS] => Boolean(l));
            return <LenderTable key={key} lenders={list} title={b.title} note={b.note} />;
          }
          case "stats":
            return (
              <div key={key} className="not-prose my-6 grid gap-3 sm:grid-cols-3">
                {b.items.map((s, j) => (
                  <div key={j} className="rounded-md border border-border bg-card p-4">
                    <p className="font-display text-xl font-extrabold text-foreground">{s.value}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
            );
        }

      })}

      <div className="not-prose my-8">
        <FeaturedBank
          lender={bottomLender}
          title={post.bottomLenderTitle ?? `Vår rekommendation – ${bottomLender.name}`}
          compact
        />
      </div>

      <LenderReviews
        lender={post.reviewsLenderId ? LENDERS[post.reviewsLenderId] : bottomLender}
      />

      {post.calculator && calculatorPosition === "afterReviews" && (
        <ArticleCalculator {...post.calculator} />
      )}

      <div className="mt-8">
        <Link
          to={post.cta.to as any}
          className="inline-flex items-center gap-2 rounded-md bg-cta px-6 py-3 text-sm font-bold !text-cta-foreground !no-underline"
        >
          {post.cta.label} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      </>
    </EpiProvider>
  );
}
